// src/components/cards/PetDetailsCard.tsx

import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Popper,
  Paper,
  ClickAwayListener,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  WarningAmber as WarningIcon,
  Pets as PetsIcon,
  Category as CategoryIcon,
  Palette as PaletteIcon,
  Wc as WcIcon,
  Cake as CakeIcon,
  Event as EventIcon,
  MedicalServices as MedicalServicesIcon,
  Vaccines as VaccinesIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Check as TrueIcon,
  Close as FalseIcon,
  Menu as MenuIcon,
  Close as CloseMenuIcon,
  Brush as SketchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Pet } from '../../services/api';
import { ResponsiveFormGrid } from '../forms/shared';
import { FloatingAccessButton } from '../common/FloatingAccessButton';
import { CardFooterContainer, CardFooter } from './shared/CardElements';
import {
  BeingDetailsField,
  MetadataField,
} from '../forms/shared/DisplayFieldVariants';
import { SpacerRow } from '../forms/shared/SpacerRow';
import { getCustomIcon } from '../../utils/customIcons';
import BottomMenu, { BottomMenuItem } from '../menus/BottomMenu';
import { useHeaderDrawer } from '../../contexts/HeaderDrawerContext';
import { useAnchorRect } from '../../hooks/useAnchorRect';

interface PetDetailsCardProps {
  pet: Pet;
  onOwnerClick?: (clientId: number) => void;
}

// Styled warning icon - matching ClientDetailsCard exactly
const StyledWarningIcon = styled(WarningIcon)(({ theme }) => ({
  fontSize: '3rem',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.main,
  marginLeft: '1.5rem',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.secondary.light
        : theme.palette.secondary.dark,
    transform: 'scale(1.1)',
  },
}));

// Styled popper content - matching ClientDetailsCard exactly
const PopperContent = styled(Paper)(({ theme }) => ({
  padding: '12px 16px',
  maxWidth: '400px',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.background.paper,
  border: '3px solid',
  borderColor:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.main
      : theme.palette.secondary.main,
  borderRadius: '8px',
  boxShadow: theme.shadows[8],
  zIndex: 1300,
}));

const PetDetailsCard: React.FC<PetDetailsCardProps> = ({
  pet,
  onOwnerClick,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isHeaderDrawerOpen } = useHeaderDrawer();

  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const [popperOpen, setPopperOpen] = useState(false);
  const [bottomMenuOpen, setBottomMenuOpen] = useState(false);
  const bottomMenuFabRef = useRef<HTMLDivElement>(null);
  const bottomMenuAnchor = useAnchorRect(bottomMenuFabRef, bottomMenuOpen);

  const handleWarningMouseEnter = (event: React.MouseEvent<SVGSVGElement>) => {
    setAnchorEl(event.currentTarget);
    setPopperOpen(true);
  };

  const handleWarningMouseLeave = () => {
    setPopperOpen(false);
  };

  const handleClickAway = () => {
    setPopperOpen(false);
  };

  const handleEditClick = () => {
    navigate(`/pets/${pet.id}/edit`);
  };

  const petMenuItems: BottomMenuItem[] = [
    {
      key: 'new-booking',
      label: 'New Booking',
      onClick: () => navigate(`/clients/${pet.clientId}/newbooking`),
    },
    {
      key: 'bookings',
      label: 'Bookings',
      onClick: () => navigate(`/clients/${pet.clientId}/bookings`),
    },
    {
      key: 'new-quote',
      label: 'New Quote',
      onClick: () => navigate(`/clients/${pet.clientId}/quote/new`),
    },
    {
      key: 'quotes',
      label: 'Quotes',
      onClick: () => navigate(`/clients/${pet.clientId}/quotations`),
    },
    {
      key: 'new-pet',
      label: 'New Pet',
      onClick: () => navigate(`/clients/${pet.clientId}/newpet`),
    },
    {
      key: 'pets',
      label: 'Pets',
      onClick: () => navigate(`/clients/${pet.clientId}/pets`),
    },
    {
      key: 'account',
      label: 'Account',
      onClick: () => navigate(`/clients/${pet.clientId}/account`),
    },
    {
      key: 'edit',
      label: `Edit ${pet.name}`,
      onClick: handleEditClick,
    },
  ];

  const calculateAge = (dateOfBirth: string): string => {
    const birth = new Date(dateOfBirth);
    const today = new Date();
    const years = today.getFullYear() - birth.getFullYear();
    const months = today.getMonth() - birth.getMonth();

    let adjustedYears = years;
    let adjustedMonths = months;

    if (months < 0) {
      adjustedYears--;
      adjustedMonths += 12;
    }

    if (adjustedYears === 0) {
      return `${adjustedMonths} month${adjustedMonths !== 1 ? 's' : ''}`;
    } else if (adjustedMonths === 0) {
      return `${adjustedYears} year${adjustedYears !== 1 ? 's' : ''}`;
    } else {
      return `${adjustedYears} year${adjustedYears !== 1 ? 's' : ''}, ${adjustedMonths} month${adjustedMonths !== 1 ? 's' : ''}`;
    }
  };

  const getPetNameAndWarningIcon = (pet: Pet) => {
    return (
      <Box display="flex" alignItems="center">
        <Typography
          component="span"
          sx={{
            fontSize: '2rem',
            fontWeight: 500,
            wordBreak: 'break-word',
            minHeight: '2.4rem',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '1rem',
            '@media (min-width: 600px)': {
              paddingLeft: '1rem',
            },
          }}
        >
          {pet.name || 'Unnamed Pet'}
        </Typography>
        {pet.showWarning && (
          <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ display: 'flex', ml: 1 }}>
              <StyledWarningIcon
                onMouseEnter={handleWarningMouseEnter}
                onMouseLeave={handleWarningMouseLeave}
                aria-label="Warning - hover for details"
              />
              <Popper
                open={popperOpen}
                anchorEl={anchorEl}
                placement="bottom"
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8],
                    },
                  },
                ]}
              >
                <PopperContent>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      fontSize: '2rem',
                      color: theme.palette.text.primary,
                      lineHeight: 1.4,
                    }}
                  >
                    {pet.notes || 'No warning notes provided'}
                  </Typography>
                </PopperContent>
              </Popper>
            </Box>
          </ClickAwayListener>
        )}
        {!pet.stillOwned && getCustomIcon('cross', '2.4rem')}
      </Box>
    );
  };

  const getPetMetadataValue = (pet: Pet) => {
    const createdDate = pet.createdAt
      ? new Date(pet.createdAt).toLocaleString()
      : 'Unknown';

    const hasUpdate =
      pet.updatedAt && pet.createdAt && pet.updatedAt !== pet.createdAt;

    const createdLine = pet.createdByUsername
      ? `Created: ${createdDate} by ${pet.createdByUsername}`
      : `Created: ${createdDate}`;

    if (hasUpdate && pet.updatedAt) {
      const updatedDate = new Date(pet.updatedAt).toLocaleString();
      const updatedLine = pet.updatedByUsername
        ? `Updated: ${updatedDate} by ${pet.updatedByUsername}`
        : `Updated: ${updatedDate}`;
      return (
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflowX: 'auto',
            overflowY: 'visible',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {createdLine}
          <br />
          {updatedLine}
        </Box>
      );
    }

    return (
      <Box component="span" sx={{ display: 'block', padding: '0.75em 0' }}>
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflowX: 'auto',
            overflowY: 'visible',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {createdLine}
        </Box>
      </Box>
    );
  };

  const getSterilisedStatus = (sterilised: boolean): string => {
    return sterilised ? 'Sterilised' : 'Not sterilised';
  };

  const getInoculationDateValue = (
    inoculationDate: string | undefined
  ): string => {
    if (!inoculationDate) {
      return 'Not provided';
    }
    return new Date(inoculationDate).toLocaleDateString();
  };

  const cardFooterProps = {
    borderTop: `3px solid ${theme.palette.grey[900]}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[600],
  };

  return (
    <>
      <CardFooterContainer sx={{ minHeight: 'calc(100vh - 220px)' }}>
        <ResponsiveFormGrid
          alternateRowColors
          breakpointPadding={{
            default: '0',
            medium: '0',
            large: '0',
          }}
          container
          spacing={0}
        >
          {/* Sketch indicator — shown only for prospect/sketch pets */}
          {pet.isSketch && (
            <BeingDetailsField
              label="Status"
              value="Sketch / Prospect Pet"
              valuePadding="2.4px 0 2.4px 1rem"
              startIcon={<SketchIcon />}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}

          {/* Name field */}
          {!isMobile && (
            <BeingDetailsField
              label="Name"
              value={getPetNameAndWarningIcon(pet)}
              hideColon
              labelFontWeight={700}
            />
          )}

          {/* Mobile shows combined Type/Breed with dynamic pet icon */}
          {isMobile ? (
            <BeingDetailsField
              label="Type / Breed"
              value={`${pet.petType?.type || 'Unknown'} / ${pet.petBreed?.breed || 'Unknown'}`}
              startIcon={getCustomIcon(pet.petType?.type, '2rem')}
              valuePaddingMobile="2.4px 0"
            />
          ) : (
            <>
              {/* Desktop: Separate Type and Breed */}
              <BeingDetailsField
                label="Type"
                value={pet.petType?.type || 'Unknown'}
                valuePadding="2.4px 0 2.4px 1rem"
                startIcon={<PetsIcon />}
                hideColon
                labelFontWeight={700}
              />
              <BeingDetailsField
                label="Breed"
                value={pet.petBreed?.breed || 'Unknown'}
                valuePadding="2.4px 0 2.4px 1rem"
                startIcon={<CategoryIcon />}
                hideColon
                labelFontWeight={700}
              />
            </>
          )}

          {/* All fields use BeingDetailsField with startIcon */}
          <BeingDetailsField
            label="Colour"
            value={pet.petColour?.colour || 'Not specified'}
            valuePadding="2.4px 0 2.4px 1rem"
            startIcon={<PaletteIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Sex"
            value={pet.gender || 'Unknown'}
            valuePadding="2.4px 0 2.4px 1rem"
            startIcon={<WcIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Age"
            valuePadding="2.4px 0 2.4px 1rem"
            value={pet.dateOfBirth ? calculateAge(pet.dateOfBirth) : 'Unknown'}
            startIcon={<EventIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Birthday"
            valuePadding="2.4px 0 2.4px 1rem"
            value={
              pet.dateOfBirth
                ? new Date(pet.dateOfBirth).toLocaleDateString()
                : 'Not provided'
            }
            startIcon={<CakeIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Sterilised"
            valuePadding="2.4px 0 2.4px 1rem"
            value={getSterilisedStatus(pet.sterilised ?? false)}
            startIcon={<MedicalServicesIcon />}
            endIcon={pet.sterilised ? <TrueIcon /> : <FalseIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Inoc. Date"
            valuePadding="2.4px 0 2.4px 1rem"
            value={getInoculationDateValue(pet.inoculationDate)}
            startIcon={<VaccinesIcon />}
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />

          <BeingDetailsField
            label="Still Owned"
            valuePadding="2.4px 0 2.4px 1rem"
            value={pet.stillOwned ? 'Still owned' : 'No longer owned'}
            startIcon={<CheckCircleIcon />}
            endIcon={
              pet.stillOwned ? (
                <TrueIcon />
              ) : (
                getCustomIcon(
                  'cross',
                  '2.4rem',
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[100]
                    : theme.palette.grey[900]
                )
              )
            }
            hideColon
            labelFontWeight={700}
            valuePaddingMobile="2.4px 0"
          />
          {pet.notes && pet.notes.trim() && (
            <BeingDetailsField
              label="Notes"
              valuePadding="2.4px 0 2.4px 1rem"
              value={pet.notes || 'No notes'}
              startIcon={<DescriptionIcon />}
              hideColon
              labelFontWeight={700}
              valuePaddingMobile="2.4px 0"
            />
          )}
          <SpacerRow
            height="2rem"
            labelHeightMobile="0"
            valueHeightMobile="1rem"
            labelBackgroundColor={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[800]
                : theme.palette.grey[400]
            }
            labelBackgroundColorMobile={
              theme.palette.mode === 'dark'
                ? theme.palette.grey[750]
                : theme.palette.grey[300]
            }
          />

          <Box sx={{ width: '100%', position: 'relative' }}>
            <MetadataField
              label={`Pet #${pet.id}`}
              labelFontSize="2rem"
              labelFontSizeMobile="1.8rem"
              labelFontWeight={700}
              valueFontSizeMobile="1.4rem"
              value={getPetMetadataValue(pet)}
              valuePaddingMobile="0.5rem 1.5rem 5rem 1.5rem"
            />
            {!isHeaderDrawerOpen && (
              <FloatingAccessButton
                ref={bottomMenuFabRef}
                icon={bottomMenuOpen ? <CloseMenuIcon /> : <MenuIcon />}
                hoverIcon={bottomMenuOpen ? <CloseMenuIcon /> : <MenuIcon />}
                onClick={() => setBottomMenuOpen((v) => !v)}
                aria-label={
                  bottomMenuOpen ? 'Close actions menu' : 'Open actions menu'
                }
                position="absolute"
                iconScale={1.5}
                backgroundColor={
                  bottomMenuOpen
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                hoverBackgroundColor={
                  bottomMenuOpen
                    ? theme.palette.primary.dark
                    : theme.palette.secondary.dark
                }
                bottom="2rem"
                right="10%"
                sx={{
                  zIndex: 1300,
                  border: '2px solid #fff',
                  '@media (max-width: 600px)': {
                    position: 'fixed',
                    bottom: '1rem',
                    right: '1rem',
                    '& svg': {
                      transform: 'scale(1.2)',
                    },
                  },
                  '@media (min-width: 601px)': {
                    width: '75px',
                    height: '75px',
                    minWidth: '75px',
                    minHeight: '75px',
                    top: 'calc(50% - 37.5px)',
                  },
                  '& svg': {
                    transition: 'transform 0.3s ease-in-out',
                    transform: 'scale(1.4)',
                  },
                  '&:active': {
                    backgroundColor: bottomMenuOpen
                      ? 'secondary.light' // Color when clicking to close
                      : 'primary.dark', // Color when clicking to open
                  },
                  '&:hover svg': {
                    opacity: 0.8,
                    transform: bottomMenuOpen
                      ? 'scale(2.1) rotate(-180deg)' // Counter-clockwise on close
                      : 'scale(2.1) rotate(180deg)', // Clockwise on open
                  },
                  // Approximate alignment with the EDIT tab in BeingsPageSubMenu.
                  // EDIT sits at the sum of preceding tab minWidths (petdetails
                  // 216 + newbooking 250 + bookings 208 + newquotation 212 +
                  // quotations 154 + newpet 176 + pets 110 + account 178 = 1504),
                  // centred over its own 110px width. Update if those tab widths
                  // change. Only applies once the tab row fits without scrolling.
                  '@media (min-width: 1700px)': {
                    left: '1559px',
                  },
                }}
              />
            )}
          </Box>
        </ResponsiveFormGrid>
        <CardFooter {...cardFooterProps} />
      </CardFooterContainer>

      <BottomMenu
        open={bottomMenuOpen}
        onClose={() => setBottomMenuOpen(false)}
        items={petMenuItems}
        right={bottomMenuAnchor ? `${bottomMenuAnchor.right}px` : '1rem'}
        bottom={bottomMenuAnchor ? `${bottomMenuAnchor.bottom}px` : '40px'}
        sx={{
          '@media (max-width: 900px)': {
            marginRight: '.1rem',
            marginBottom: '.1rem',
          },
          '@media (min-width: 900px)': {
            marginRight: '1rem',
            marginBottom: '1rem',
          },
        }}
      />
    </>
  );
};

export default PetDetailsCard;
